import Joi from "joi-browser";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { xml2js } from "../../json/xml2js";
import {
  cloneForm,
  compareForms,
  deleteFormSubmission,
  getCareTeams,
  getClientData,
  getConfig,
  getFieldLabel,
  getFieldOptions,
  getFieldValue,
  getFormsySubmissionsForClient,
  getSubmission,
  handleCopyUrl,
  handleFormChange,
  saveForm,
  sendShareEmail,
  validateForm,
} from "../../scripts/formScriptsv2";
import {
  ArrayCheck,
  getFieldDate,
  getFieldName,
  getFieldText,
} from "../../scripts/helperScripts";
import BackIcon from "../common/BackIcon";
import ClientFormHeader from "../common/ClientFormHeader";
import Comment from "../common/Comment";
import {
  FormCheckbox,
  FormInput,
  FormRadioButton,
  FormSelect,
} from "../common/ControlBundle";
import FormButtonBar from "../common/FormButtonBar";
import ShareModal from "../common/ShareModal";
import Signature from "../common/Signature";
import getAge from "../services/ageService";
import { crisisReferrals } from "../services/soar";

function COMC18Assessment() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  const [loadStatus, setLoadStatus] = useState({});
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = useMemo(() => {
    const selectedProgram = clientData.programs?.find(
      (p) => p.ccx_client_programid === data.SelectedProgram?.value
    );
    let admitDate = selectedProgram
      ? moment(getFieldText(selectedProgram.ccx_admitdate))
      : moment();

    return {
      SelectedProgram: Joi.string().required(),
      CrisisReferral: admitDate.isBefore(moment("2025/4/1"))
        ? Joi.string()
        : Joi.string().required(),
      Address: Joi.string().required(),
      Location: Joi.string().required(),
      Pronouns: Joi.string().required(),
      Caregiver: Joi.string().required(),
      Language: Joi.string().required(),
      ClientPerspective: Joi.string().required(),
      CollateralPerspective: Joi.string().required(),
      Psychiatric: Joi.boolean(),
      PsychiatricDetails: Joi.when("Psychiatric", {
        is: true,
        then: Joi.string().required().label("Psychiatric details"),
      }),
      Substances: Joi.boolean(),
      SubstancesDetails: Joi.when("Substances", {
        is: true,
        then: Joi.string().required().label("Substance use  details"),
      }),
      Trauma: Joi.boolean(),
      TraumaDetails: Joi.when("Trauma", {
        is: true,
        then: Joi.string().required().label("Trauma details"),
      }),
      Relationships: Joi.boolean(),
      RelationshipsDetails: Joi.when("Relationships", {
        is: true,
        then: Joi.string()
          .required()
          .label("Family and social relationships details"),
      }),
      Expectations: Joi.boolean(),
      ExpectationsDetails: Joi.when("Expectations", {
        is: true,
        then: Joi.string()
          .required()
          .label("3rd party/Caregiver expectations and needs details"),
      }),
      Social: Joi.boolean(),
      SocialDetails: Joi.when("Social", {
        is: true,
        then: Joi.string().required().label("Social details"),
      }),
      Legal: Joi.boolean(),
      LegalDetails: Joi.when("Legal", {
        is: true,
        then: Joi.string().required().label("Legal details"),
      }),
      Occupational: Joi.boolean(),
      OccupationalDetails: Joi.when("Occupational", {
        is: true,
        then: Joi.string().required().label("Occupational/work/school details"),
      }),
      Housing: Joi.boolean(),
      HousingDetails: Joi.when("Housing", {
        is: true,
        then: Joi.string().required().label("Housing/food Security details"),
      }),
      Cognitive: Joi.boolean(),
      CognitiveDetails: Joi.when("Cognitive", {
        is: true,
        then: Joi.string()
          .required()
          .label("Cognitive development challenges details"),
      }),
      Education: Joi.boolean(),
      EducationDetails: Joi.when("Education", {
        is: true,
        then: Joi.string()
          .required()
          .label("Education/military/vocational training details"),
      }),
      AbuseRisk: Joi.string().required(),
      AbuseRiskDetails: Joi.when("AbuseRisk", {
        is: "Yes",
        then: Joi.string().required(),
      }),
      JobThreat: Joi.when("AbuseRisk", {
        is: "Yes",
        then: Joi.string().required(),
      }),
      Forced: Joi.when("AbuseRisk", {
        is: "Yes",
        then: Joi.string().required(),
      }),
      Physical: Joi.when("AbuseRisk", {
        is: "Yes",
        then: Joi.string().required(),
      }),
      Insulting: Joi.when("AbuseRisk", {
        is: "Yes",
        then: Joi.string().required(),
      }),
      MedicalConcerns: Joi.string().required(),
      CurrentProviders: Joi.string().required(),
      CurrentMeds: Joi.string().required(),
      Pregnancy: Joi.string(),
      PAD: Joi.string().required(),
      PreviousDiagnosis: Joi.string().required(),
      Inpatient: Joi.string().required(),
      BHProviders: Joi.string().required(),
      Danger: Joi.string().required(),
      DangerDetails: Joi.when("Danger", {
        is: "Yes",
        then: Joi.string().required(),
      }),
      DutyToWarn: Joi.string().required(),
      DutyToWarnDetails: Joi.when("DutyToWarn", {
        is: "Yes",
        then: Joi.string().required(),
      }),
      PreviousAttempt: Joi.string().required(),
      PreviousAttemptResult: Joi.when("PreviousAttempt", {
        is: "Yes",
        then: Joi.string().required(),
      }),
      InDanger: Joi.string().required(),
      InDangerDetails: Joi.when("InDanger", {
        is: "Yes",
        then: Joi.string().required(),
      }),
      Safe: Joi.string().required(),
      SafeDetails: Joi.when("Safe", {
        is: "No",
        then: Joi.string().required(),
      }),
      Capacity: Joi.string().required(),
      Cultural: Joi.string().required(),
      ElderlyIssues: Joi.string().required(),
      ChildIssues: Joi.string().required(),
      SocialSupport: Joi.string().required(),
      SocialSupportDetails: Joi.string().required(),
      PhysicalHealth: Joi.string().required(),
      PhysicalHealthDetails: Joi.string().required(),
      Purpose: Joi.string().required(),
      PurposeDetails: Joi.string().required(),
      Coping: Joi.string().required(),
      CopingDetails: Joi.string().required(),
      Thinking: Joi.string().required(),
      ThinkingDetails: Joi.string().required(),
      SelfEsteem: Joi.string().required(),
      SelfEsteemDetails: Joi.string().required(),
      SelfEfficacy: Joi.string().required(),
      SelfEfficacyDetails: Joi.string().required(),
      OtherStrengths: Joi.string().allow(""),
      OutOfHome: Joi.string().required(),
      Strengths: Joi.string().required(),
      Coordination: Joi.string().required(),
      Considerations: Joi.string().required(),
      Desire: Joi.string().required(),
      Summary: Joi.string().required(),
      Interventions: Joi.string().required(),
      SafetyPlan: Joi.string().required(),
      ROI: Joi.string().required(),
      MeansRestriction: Joi.string().required(),
      M1Need: Joi.string().required(),
      CareCoordination: Joi.string().required(),
      BHAppointment: Joi.string().required(),
      LevelOfCare: Joi.string().required(),
      Transportation: Joi.when("LevelOfCare", {
        is: Joi.string().valid(
          "WIC",
          "CSU",
          "ATU",
          "ED",
          "Inpatient treatment"
        ),
        then: Joi.string().required(),
      }),
      CalledPolice: Joi.when("LevelOfCare", {
        is: Joi.string().valid(
          "WIC",
          "CSU",
          "ATU",
          "ED",
          "Inpatient treatment"
        ),
        then: Joi.when("Transportation", {
          is: "Law Enforcement",
          then: Joi.string().required(),
        }),
      }),
      ModalityPhone: Joi.when("LevelOfCare", {
        is: Joi.string().valid(
          "WIC",
          "CSU",
          "ATU",
          "ED",
          "Inpatient treatment"
        ),
        then: Joi.when("Transportation", {
          is: "Modality",
          then: Joi.string().required(),
        }),
      }),
      CalledFollowUp: Joi.string().required(),
      AdditionalNotes: Joi.string().allow(""),
    };
  }, [data.SelectedProgram?.value, clientData.programs]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  const formConfig = useMemo(() => {
    return formsyConfig.FormConfiguration?.[0].config[0] || {};
  }, [formsyConfig]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (initialProgram?.ccx_client_programid && loadStatus.dataSet) {
      setData((d) => {
        const obj = { ...d };

        if (
          obj.SelectedProgram === undefined ||
          getFieldValue(obj.SelectedProgram)
        ) {
          return obj;
        }

        obj.SelectedProgram.value = initialProgram.ccx_client_programid;
        return obj;
      });

      setFormsyDetails((fd) => {
        const details = { ...fd };

        if (details.ClientProgramId === initialProgram.ccx_client_programid) {
          return details;
        }

        details.ClientProgramId = initialProgram.ccx_client_programid;
        details.ClientProgramName = getFieldName(
          initialProgram.ccx_programunit
        );
        return details;
      });
    }
  }, [initialProgram, loadStatus.dataSet]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }

        setLoadStatus((ls) => {
          return { ...ls, dataSet: true };
        });
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      !safyInfo?.user ||
      loadStatus.configLoaded ||
      loadStatus.loadingConfig
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadStatus((ls) => {
        return { ...ls, loadingConfig: true };
      });
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadStatus((ls) => {
          return {
            ...ls,
            loadingConfig: false,
            configLoaded: true,
            dataSet: true,
          };
        });
      } else {
        setLoadStatus((ls) => {
          return { ...ls, loadingConfig: false, configLoaded: true };
        });
      }
    };

    loadConfig();
  }, [
    formid,
    clientid,
    safyInfo,
    submissionid,
    loadStatus.configLoaded,
    loadStatus.loadingConfig,
  ]);

  const helperConfiguration = useMemo(() => {
    const defaultConfig = {
      buttonBarConfig: {
        sign: {
          display: false,
          initialSignatureType: "full",
        },
        clone: {
          display: false,
        },
        delete: {
          display: false,
        },
        share: {
          display: false,
        },
        comment: {
          display: false,
        },
      },
      signatureType: "full",
      finalSignType: "Provider",
    };
    if (!formsyConfig.HelperConfiguration?.[0]) {
      return defaultConfig;
    }
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      console.log(
        "helperConfiguration JSON:",
        formsyConfig.HelperConfiguration[0]
      );
      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.find(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      );

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments; // commentsOverride is usually null, but specifically undefined during Final Signing
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.value) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property].type !== "section" &&
        data[property].type !== "label" &&
        data[property].type !== "User" &&
        data[property].type !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            getFieldLabel(data[property]) !== undefined &&
            getFieldLabel(data[property]) !== ""
              ? getFieldLabel(data[property])
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
      setCommentToEdit(undefined);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    if (helperConfiguration.finalSignType === "Any") {
      return true;
    }

    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");
    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );
    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  useEffect(() => {
    if (data.SelectedProgram?.value) {
      let ignore = false;
      const getCrisisReferral = async () => {
        const body = {
          command: "query",
          application:
            process.env.REACT_APP_ENVIRONMENT === "Production"
              ? "5DAE139E-D052-4BD8-BF4A-499A31AC3C15"
              : "58990F83-8566-4BA8-B4B5-584A64E4CBA1",
          keyword: "byclientprogram",
          ids: [data.SelectedProgram.value],
        };
        const response = await crisisReferrals(body);

        if (!ignore && response.data?.[0]) {
          const referral = response.data[0];
          setData((d) => {
            const obj = { ...d };
            obj.CrisisReferral.value = referral.id;
            obj.CrisisReferral.name = referral.clientName;
            obj.CrisisReferral.date = moment(referral.referralDateTime).format(
              "l"
            );
            return obj;
          });
        }
      };

      getCrisisReferral();

      return () => (ignore = true);
    }
  }, [data.SelectedProgram?.value]);

  const customValidate = (errs) => {
    if (
      (!getFieldValue(data.Psychiatric) ||
        getFieldValue(data.Psychiatric) === "false") &&
      (!getFieldValue(data.Substances) ||
        getFieldValue(data.Substances) === "false") &&
      (!getFieldValue(data.Trauma) || getFieldValue(data.Trauma) === "false") &&
      (!getFieldValue(data.Relationships) ||
        getFieldValue(data.Relationships) === "false") &&
      (!getFieldValue(data.Expectations) ||
        getFieldValue(data.Expectations) === "false") &&
      (!getFieldValue(data.Social) || getFieldValue(data.Social) === "false") &&
      (!getFieldValue(data.Legal) || getFieldValue(data.Legal) === "false") &&
      (!getFieldValue(data.Occupational) ||
        getFieldValue(data.Occupational) === "false") &&
      (!getFieldValue(data.Housing) ||
        getFieldValue(data.Housing) === "false") &&
      (!getFieldValue(data.Cognitive) ||
        getFieldValue(data.Cognitive) === "false") &&
      (!getFieldValue(data.Education) ||
        getFieldValue(data.Education) === "false") &&
      (!data.NotApplicable?.value ||
        getFieldValue(data.NotApplicable) === "false")
    ) {
      if (errs) errs.RiskFactors = "At least one Risk Factor is required";
      else
        errs = {
          RiskFactors: "At least one Risk Factor is required",
        };
    }

    setErrors(errs);
    return errs;
  };
  const yesNoOptions = [
    { _id: "Yes", name: "Yes" },
    { _id: "No", name: "No" },
  ];

  const strengthOptions = [
    { _id: "Weak", name: "Weak" },
    { _id: "Moderate", name: "Moderate" },
    { _id: "Strong", name: "Strong" },
  ];

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        />
        <FormButtonBar
          config={helperConfiguration.buttonBarConfig}
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
          customValidate={customValidate}
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {loadStatus.dataSet && loadStatus.configLoaded ? (
            <>
              <div className="row justify-content-between">
                <div className="col-auto">
                  <label
                    htmlFor="crisis-referral"
                    className="me-2 mb-2 fw-bold"
                  >
                    {getFieldLabel(formConfig.CrisisReferral)}
                  </label>
                  <Link
                    id="crisis-referral"
                    to={`/crisis/${data.CrisisReferral.value}`}
                    target="_blank"
                  >
                    {data.CrisisReferral.name} - {data.CrisisReferral.date}
                  </Link>
                </div>
                {submissionid ? (
                  <div className="col-auto">
                    <button
                      className="btn btn-link d-print-none"
                      title="Copy and Open Encounter"
                      onClick={() => {
                        const encounterEnvironment =
                          process.env.REACT_APP_ENVIRONMENT === "Production"
                            ? "ccplive-cp"
                            : "d365base-cp";
                        handleCopyUrl(location.pathname).then(() => {
                          window.open(
                            `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                          );
                        });
                      }}
                    >
                      Copy form link and launch to Encounter
                    </button>
                  </div>
                ) : null}
              </div>
              <div>
                <div className="mb-4">
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.Demographics)}
                  </div>
                  <div>
                    <span className="fw-bold">
                      {getFieldLabel(formConfig.Name)}
                    </span>{" "}
                    {clientData.client?.fullName}
                  </div>
                  <div>
                    <span className="fw-bold">
                      {getFieldLabel(formConfig.DOB)}
                    </span>{" "}
                    {getFieldDate(clientData.client?.dob)}
                  </div>
                  <div>
                    <span className="fw-bold">
                      {getFieldLabel(formConfig.Age)}
                    </span>{" "}
                    {getAge(getFieldDate(clientData.client?.dob))}
                  </div>
                  <div>
                    <FormInput
                      name="Address"
                      label={getFieldLabel(formConfig.Address)}
                      error={errors.Address}
                      value={getFieldValue(data.Address)}
                      onChange={handleChange}
                      identical={compare("Address")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="Location"
                      label={getFieldLabel(formConfig.Location)}
                      error={errors.Location}
                      value={getFieldValue(data.Location)}
                      onChange={handleChange}
                      identical={compare("Location")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="Pronouns"
                      label={getFieldLabel(formConfig.Pronouns)}
                      error={errors.Pronouns}
                      value={getFieldValue(data.Pronouns)}
                      onChange={handleChange}
                      identical={compare("Pronouns")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="Caregiver"
                      label={getFieldLabel(formConfig.Caregiver)}
                      error={errors.Caregiver}
                      value={getFieldValue(data.Caregiver)}
                      onChange={handleChange}
                      identical={compare("Caregiver")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      placeholder={formConfig.Caregiver?.placeholder}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <FormInput
                    name="Language"
                    label={getFieldLabel(formConfig.Language)}
                    value={getFieldValue(data.Language)}
                    onChange={handleChange}
                    identical={compare("Language")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    labelClassName="fw-bold"
                  />
                  {errors.Language ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Language}
                    </div>
                  ) : null}
                </div>
                <div className="mb-4">
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.Problem)}
                  </div>
                  <div>
                    <FormInput
                      name="ClientPerspective"
                      label={getFieldLabel(formConfig.ClientPerspective)}
                      error={errors.ClientPerspective}
                      value={getFieldValue(data.ClientPerspective)}
                      onChange={handleChange}
                      identical={compare("ClientPerspective")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="CollateralPerspective"
                      label={getFieldLabel(formConfig.CollateralPerspective)}
                      error={errors.CollateralPerspective}
                      value={getFieldValue(data.CollateralPerspective)}
                      onChange={handleChange}
                      identical={compare("CollateralPerspective")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.RiskFactors)}
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Psychiatric"
                        label={getFieldLabel(formConfig.Psychiatric)}
                        checked={getFieldValue(data.Psychiatric) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Psychiatric.value === "true" ? (
                        <FormInput
                          name="PsychiatricDetails"
                          label={getFieldLabel(formConfig.PsychiatricDetails)}
                          error={errors.PsychiatricDetails}
                          value={getFieldValue(data.PsychiatricDetails)}
                          onChange={handleChange}
                          identical={compare("PsychiatricDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Substances"
                        label={getFieldLabel(formConfig.Substances)}
                        checked={getFieldValue(data.Substances) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Substances.value === "true" ? (
                        <FormInput
                          name="SubstancesDetails"
                          label={getFieldLabel(formConfig.SubstancesDetails)}
                          error={errors.SubstancesDetails}
                          value={getFieldValue(data.SubstancesDetails)}
                          onChange={handleChange}
                          identical={compare("SubstancesDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Trauma"
                        label={getFieldLabel(formConfig.Trauma)}
                        checked={getFieldValue(data.Trauma) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Trauma.value === "true" ? (
                        <FormInput
                          name="TraumaDetails"
                          label={getFieldLabel(formConfig.TraumaDetails)}
                          error={errors.TraumaDetails}
                          value={getFieldValue(data.TraumaDetails)}
                          onChange={handleChange}
                          identical={compare("TraumaDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Relationships"
                        label={getFieldLabel(formConfig.Relationships)}
                        checked={getFieldValue(data.Relationships) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Relationships.value === "true" ? (
                        <FormInput
                          name="RelationshipsDetails"
                          label={getFieldLabel(formConfig.RelationshipsDetails)}
                          error={errors.RelationshipsDetails}
                          value={getFieldValue(data.RelationshipsDetails)}
                          onChange={handleChange}
                          identical={compare("RelationshipsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Expectations"
                        label={getFieldLabel(formConfig.Expectations)}
                        checked={getFieldValue(data.Expectations) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Expectations.value === "true" ? (
                        <FormInput
                          name="ExpectationsDetails"
                          label={getFieldLabel(formConfig.ExpectationsDetails)}
                          error={errors.ExpectationsDetails}
                          value={getFieldValue(data.ExpectationsDetails)}
                          onChange={handleChange}
                          identical={compare("ExpectationsDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Social"
                        label={getFieldLabel(formConfig.Social)}
                        checked={getFieldValue(data.Social) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Social.value === "true" ? (
                        <FormInput
                          name="SocialDetails"
                          label={getFieldLabel(formConfig.SocialDetails)}
                          error={errors.SocialDetails}
                          value={getFieldValue(data.SocialDetails)}
                          onChange={handleChange}
                          identical={compare("SocialDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Legal"
                        label={getFieldLabel(formConfig.Legal)}
                        checked={getFieldValue(data.Legal) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Legal.value === "true" ? (
                        <FormInput
                          name="LegalDetails"
                          label={getFieldLabel(formConfig.LegalDetails)}
                          error={errors.LegalDetails}
                          value={getFieldValue(data.LegalDetails)}
                          onChange={handleChange}
                          identical={compare("LegalDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Occupational"
                        label={getFieldLabel(formConfig.Occupational)}
                        checked={getFieldValue(data.Occupational) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Occupational.value === "true" ? (
                        <FormInput
                          name="OccupationalDetails"
                          label={getFieldLabel(formConfig.OccupationalDetails)}
                          error={errors.OccupationalDetails}
                          value={getFieldValue(data.OccupationalDetails)}
                          onChange={handleChange}
                          identical={compare("OccupationalDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Housing"
                        label={getFieldLabel(formConfig.Housing)}
                        checked={getFieldValue(data.Housing) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Housing.value === "true" ? (
                        <FormInput
                          name="HousingDetails"
                          label={getFieldLabel(formConfig.HousingDetails)}
                          error={errors.HousingDetails}
                          value={getFieldValue(data.HousingDetails)}
                          onChange={handleChange}
                          identical={compare("HousingDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Cognitive"
                        label={getFieldLabel(formConfig.Cognitive)}
                        checked={getFieldValue(data.Cognitive) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Cognitive.value === "true" ? (
                        <FormInput
                          name="CognitiveDetails"
                          label={getFieldLabel(formConfig.CognitiveDetails)}
                          error={errors.CognitiveDetails}
                          value={getFieldValue(data.CognitiveDetails)}
                          onChange={handleChange}
                          identical={compare("CognitiveDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormCheckbox
                        name="Education"
                        label={getFieldLabel(formConfig.Education)}
                        checked={getFieldValue(data.Education) === "true"}
                        onChange={handleChange}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        divClassName="ms-3"
                      />
                    </div>
                    <div className="col">
                      {data.Education.value === "true" ? (
                        <FormInput
                          name="EducationDetails"
                          label={getFieldLabel(formConfig.EducationDetails)}
                          error={errors.EducationDetails}
                          value={getFieldValue(data.EducationDetails)}
                          onChange={handleChange}
                          identical={compare("EducationDetails")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      ) : null}
                    </div>
                  </div>
                  {errors.RiskFactors ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.RiskFactors}
                    </div>
                  ) : null}
                </div>
                <div className="mb-4">
                  <FormRadioButton
                    name="AbuseRisk"
                    label={getFieldLabel(formConfig.AbuseRisk)}
                    value={getFieldValue(data.AbuseRisk)}
                    options={yesNoOptions}
                    onChange={handleChange}
                    identical={compare("AbuseRisk")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    labelClassName="fw-bold"
                  />
                  {errors.AbuseRisk ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.AbuseRisk}
                    </div>
                  ) : null}
                  {getFieldValue(data.AbuseRisk) === "Yes" ? (
                    <>
                      <FormInput
                        name="AbuseRiskDetails"
                        label={getFieldLabel(formConfig.AbuseRiskDetails)}
                        error={errors.AbuseRiskDetails}
                        value={getFieldValue(data.AbuseRiskDetails)}
                        onChange={handleChange}
                        identical={compare("AbuseRiskDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <div type="a">
                        <div>
                          <FormRadioButton
                            name="JobThreat"
                            label={getFieldLabel(formConfig.JobThreat)}
                            error={errors.JobThreat}
                            value={getFieldValue(data.JobThreat)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            identical={compare("JobThreat")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div>
                          <FormRadioButton
                            name="Forced"
                            label={getFieldLabel(formConfig.Forced)}
                            error={errors.Forced}
                            value={getFieldValue(data.Forced)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            identical={compare("Forced")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div>
                          <FormRadioButton
                            name="Physical"
                            label={getFieldLabel(formConfig.Physical)}
                            error={errors.Physical}
                            value={getFieldValue(data.Physical)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            identical={compare("Physical")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                        <div>
                          <FormRadioButton
                            name="Insulting"
                            label={getFieldLabel(formConfig.Insulting)}
                            error={errors.Insulting}
                            value={getFieldValue(data.Insulting)}
                            options={yesNoOptions}
                            onChange={handleChange}
                            identical={compare("Insulting")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        </div>
                      </div>
                    </>
                  ) : null}
                </div>
                <div className="mb-4">
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.HealthStatus)}
                  </div>
                  <div>
                    <FormInput
                      name="MedicalConcerns"
                      label={getFieldLabel(formConfig.MedicalConcerns)}
                      error={errors.MedicalConcerns}
                      value={getFieldValue(data.MedicalConcerns)}
                      onChange={handleChange}
                      identical={compare("MedicalConcerns")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="CurrentProviders"
                      label={getFieldLabel(formConfig.CurrentProviders)}
                      error={errors.CurrentProviders}
                      value={getFieldValue(data.CurrentProviders)}
                      onChange={handleChange}
                      identical={compare("CurrentProviders")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="CurrentMeds"
                      label={getFieldLabel(formConfig.CurrentMeds)}
                      error={errors.CurrentMeds}
                      value={getFieldValue(data.CurrentMeds)}
                      onChange={handleChange}
                      identical={compare("CurrentMeds")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="Pregnancy"
                      label={getFieldLabel(formConfig.Pregnancy)}
                      error={errors.Pregnancy}
                      value={getFieldValue(data.Pregnancy)}
                      onChange={handleChange}
                      identical={compare("Pregnancy")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="PAD"
                      label={getFieldLabel(formConfig.PAD)}
                      error={errors.PAD}
                      value={getFieldValue(data.PAD)}
                      onChange={handleChange}
                      identical={compare("PAD")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.Criteria)}
                  </div>
                  <div>
                    <FormInput
                      name="PreviousDiagnosis"
                      label={getFieldLabel(formConfig.PreviousDiagnosis)}
                      error={errors.PreviousDiagnosis}
                      value={getFieldValue(data.PreviousDiagnosis)}
                      onChange={handleChange}
                      identical={compare("PreviousDiagnosis")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="Inpatient"
                      label={getFieldLabel(formConfig.Inpatient)}
                      error={errors.Inpatient}
                      value={getFieldValue(data.Inpatient)}
                      onChange={handleChange}
                      identical={compare("Inpatient")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="BHProviders"
                      label={getFieldLabel(formConfig.BHProviders)}
                      error={errors.BHProviders}
                      value={getFieldValue(data.BHProviders)}
                      onChange={handleChange}
                      identical={compare("BHProviders")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.MentalExam)}
                  </div>
                  <div className="fst-italic">
                    {formConfig.MentalExam?.subLabel}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.Suicide)}
                  </div>
                  <div className="fst-italic">
                    {formConfig.Suicide?.subLabel}
                  </div>
                </div>
                <div className="mb-4">
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.Safety)}
                  </div>
                  <div>
                    <FormRadioButton
                      name="Danger"
                      label={getFieldLabel(formConfig.Danger)}
                      error={errors.Danger}
                      value={getFieldValue(data.Danger)}
                      options={yesNoOptions}
                      onChange={handleChange}
                      identical={compare("Danger")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {getFieldValue(data.Danger) === "Yes" ? (
                      <FormInput
                        name="DangerDetails"
                        label={getFieldLabel(formConfig.DangerDetails)}
                        error={errors.DangerDetails}
                        value={getFieldValue(data.DangerDetails)}
                        onChange={handleChange}
                        identical={compare("DangerDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                  <div>
                    <FormRadioButton
                      name="DutyToWarn"
                      label={getFieldLabel(formConfig.DutyToWarn)}
                      error={errors.DutyToWarn}
                      value={getFieldValue(data.DutyToWarn)}
                      options={yesNoOptions}
                      onChange={handleChange}
                      identical={compare("DutyToWarn")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {getFieldValue(data.DutyToWarn) === "Yes" ? (
                      <FormInput
                        name="DutyToWarnDetails"
                        label={getFieldLabel(formConfig.DutyToWarnDetails)}
                        error={errors.DutyToWarnDetails}
                        value={getFieldValue(data.DutyToWarnDetails)}
                        onChange={handleChange}
                        identical={compare("DutyToWarnDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                  <div>
                    <FormRadioButton
                      name="PreviousAttempt"
                      label={getFieldLabel(formConfig.PreviousAttempt)}
                      error={errors.PreviousAttempt}
                      value={getFieldValue(data.PreviousAttempt)}
                      options={yesNoOptions}
                      onChange={handleChange}
                      identical={compare("PreviousAttempt")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {getFieldValue(data.PreviousAttempt) === "Yes" ? (
                      <FormInput
                        name="PreviousAttemptResult"
                        label={getFieldLabel(formConfig.PreviousAttemptResult)}
                        error={errors.PreviousAttemptResult}
                        value={getFieldValue(data.PreviousAttemptResult)}
                        onChange={handleChange}
                        identical={compare("PreviousAttemptResult")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                  <div>
                    <FormRadioButton
                      name="InDanger"
                      label={getFieldLabel(formConfig.InDanger)}
                      error={errors.InDanger}
                      value={getFieldValue(data.InDanger)}
                      options={yesNoOptions}
                      onChange={handleChange}
                      identical={compare("InDanger")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {getFieldValue(data.InDanger) === "Yes" ? (
                      <FormInput
                        name="InDangerDetails"
                        label={getFieldLabel(formConfig.InDangerDetails)}
                        error={errors.InDangerDetails}
                        value={getFieldValue(data.InDangerDetails)}
                        onChange={handleChange}
                        identical={compare("InDangerDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                  <div>
                    <FormRadioButton
                      name="Safe"
                      label={getFieldLabel(formConfig.Safe)}
                      error={errors.Safe}
                      value={getFieldValue(data.Safe)}
                      options={yesNoOptions}
                      onChange={handleChange}
                      identical={compare("Safe")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {getFieldValue(data.Safe) === "No" ? (
                      <FormInput
                        name="SafeDetails"
                        label={getFieldLabel(formConfig.SafeDetails)}
                        error={errors.SafeDetails}
                        value={getFieldValue(data.SafeDetails)}
                        onChange={handleChange}
                        identical={compare("SafeDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    ) : null}
                  </div>
                </div>
                <div className="mb-4">
                  <FormInput
                    name="Capacity"
                    label={getFieldLabel(formConfig.Capacity)}
                    value={getFieldValue(data.Capacity)}
                    onChange={handleChange}
                    identical={compare("Capacity")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    labelClassName="fw-bold"
                  />
                  {errors.Capacity ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Capacity}
                    </div>
                  ) : null}
                </div>
                <div className="mb-4">
                  <FormInput
                    name="Cultural"
                    label={getFieldLabel(formConfig.Cultural)}
                    value={getFieldValue(data.Cultural)}
                    onChange={handleChange}
                    identical={compare("Cultural")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    labelClassName="fw-bold"
                  />
                  {errors.Cultural ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.Cultural}
                    </div>
                  ) : null}
                </div>
                <div className="mb-4">
                  <FormInput
                    name="ElderlyIssues"
                    label={getFieldLabel(formConfig.ElderlyIssues)}
                    value={getFieldValue(data.ElderlyIssues)}
                    onChange={handleChange}
                    identical={compare("ElderlyIssues")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    labelClassName="fw-bold"
                  />
                  {errors.ElderlyIssues ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.ElderlyIssues}
                    </div>
                  ) : null}
                </div>
                <div className="mb-4">
                  <FormInput
                    name="ChildIssues"
                    label={getFieldLabel(formConfig.ChildIssues)}
                    value={getFieldValue(data.ChildIssues)}
                    onChange={handleChange}
                    identical={compare("ChildIssues")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                    labelClassName="fw-bold"
                  />
                  {errors.ChildIssues ? (
                    <div className="mt-1 alert alert-danger">
                      {errors.ChildIssues}
                    </div>
                  ) : null}
                </div>
                <div className="mb-4">
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.Strengths)}
                  </div>
                  <div className="row row-cols-1 row-cols-lg-2 align-items-end">
                    <div className="col">
                      <FormRadioButton
                        name="SocialSupport"
                        label={getFieldLabel(formConfig.SocialSupport)}
                        error={errors.SocialSupport}
                        value={getFieldValue(data.SocialSupport)}
                        options={strengthOptions}
                        onChange={handleChange}
                        identical={compare("SocialSupport")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="SocialSupportDetails"
                        label={getFieldLabel(formConfig.SocialSupportDetails)}
                        error={errors.SocialSupportDetails}
                        value={getFieldValue(data.SocialSupportDetails)}
                        onChange={handleChange}
                        identical={compare("SocialSupportDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        customMarginBottom="mb-0"
                      />
                    </div>
                  </div>
                  <div className="row row-cols-1 row-cols-lg-2 align-items-end">
                    <div className="col">
                      <FormRadioButton
                        name="PhysicalHealth"
                        label={getFieldLabel(formConfig.PhysicalHealth)}
                        error={errors.PhysicalHealth}
                        value={getFieldValue(data.PhysicalHealth)}
                        options={strengthOptions}
                        onChange={handleChange}
                        identical={compare("PhysicalHealth")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="PhysicalHealthDetails"
                        label={getFieldLabel(formConfig.PhysicalHealthDetails)}
                        error={errors.PhysicalHealthDetails}
                        value={getFieldValue(data.PhysicalHealthDetails)}
                        onChange={handleChange}
                        identical={compare("PhysicalHealthDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        customMarginBottom="mb-0"
                      />
                    </div>
                  </div>
                  <div className="row row-cols-1 row-cols-lg-2 align-items-end">
                    <div className="col">
                      <FormRadioButton
                        name="Purpose"
                        label={getFieldLabel(formConfig.Purpose)}
                        error={errors.Purpose}
                        value={getFieldValue(data.Purpose)}
                        options={strengthOptions}
                        onChange={handleChange}
                        identical={compare("Purpose")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="PurposeDetails"
                        label={getFieldLabel(formConfig.PurposeDetails)}
                        error={errors.PurposeDetails}
                        value={getFieldValue(data.PurposeDetails)}
                        onChange={handleChange}
                        identical={compare("PurposeDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        customMarginBottom="mb-0"
                      />
                    </div>
                  </div>
                  <div className="row row-cols-1 row-cols-lg-2 align-items-end">
                    <div className="col">
                      <FormRadioButton
                        name="Coping"
                        label={getFieldLabel(formConfig.Coping)}
                        error={errors.Coping}
                        value={getFieldValue(data.Coping)}
                        options={strengthOptions}
                        onChange={handleChange}
                        identical={compare("Coping")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="CopingDetails"
                        label={getFieldLabel(formConfig.CopingDetails)}
                        error={errors.CopingDetails}
                        value={getFieldValue(data.CopingDetails)}
                        onChange={handleChange}
                        identical={compare("CopingDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        customMarginBottom="mb-0"
                      />
                    </div>
                  </div>
                  <div className="row row-cols-1 row-cols-lg-2 align-items-end">
                    <div className="col">
                      <FormRadioButton
                        name="Thinking"
                        label={getFieldLabel(formConfig.Thinking)}
                        error={errors.Thinking}
                        value={getFieldValue(data.Thinking)}
                        options={strengthOptions}
                        onChange={handleChange}
                        identical={compare("Thinking")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="ThinkingDetails"
                        label={getFieldLabel(formConfig.ThinkingDetails)}
                        error={errors.ThinkingDetails}
                        value={getFieldValue(data.ThinkingDetails)}
                        onChange={handleChange}
                        identical={compare("ThinkingDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        customMarginBottom="mb-0"
                      />
                    </div>
                  </div>
                  <div className="row row-cols-1 row-cols-lg-2 align-items-end">
                    <div className="col">
                      <FormRadioButton
                        name="SelfEsteem"
                        label={getFieldLabel(formConfig.SelfEsteem)}
                        error={errors.SelfEsteem}
                        value={getFieldValue(data.SelfEsteem)}
                        options={strengthOptions}
                        onChange={handleChange}
                        identical={compare("SelfEsteem")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="SelfEsteemDetails"
                        label={getFieldLabel(formConfig.SelfEsteemDetails)}
                        error={errors.SelfEsteemDetails}
                        value={getFieldValue(data.SelfEsteemDetails)}
                        onChange={handleChange}
                        identical={compare("SelfEsteemDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        customMarginBottom="mb-0"
                      />
                    </div>
                  </div>
                  <div className="row row-cols-1 row-cols-lg-2 align-items-end">
                    <div className="col">
                      <FormRadioButton
                        name="SelfEfficacy"
                        label={getFieldLabel(formConfig.SelfEfficacy)}
                        error={errors.SelfEfficacy}
                        value={getFieldValue(data.SelfEfficacy)}
                        options={strengthOptions}
                        onChange={handleChange}
                        identical={compare("SelfEfficacy")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                    <div className="col">
                      <FormInput
                        name="SelfEfficacyDetails"
                        label={getFieldLabel(formConfig.SelfEfficacyDetails)}
                        error={errors.SelfEfficacyDetails}
                        value={getFieldValue(data.SelfEfficacyDetails)}
                        onChange={handleChange}
                        identical={compare("SelfEfficacyDetails")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        customMarginBottom="mb-0"
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col">
                      <FormInput
                        name="OtherStrengths"
                        label={getFieldLabel(formConfig.OtherStrengths)}
                        error={errors.OtherStrengths}
                        value={getFieldValue(data.OtherStrengths)}
                        onChange={handleChange}
                        identical={compare("OtherStrengths")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.Barriers)}
                  </div>
                  <div>
                    <FormInput
                      name="OutOfHome"
                      label={getFieldLabel(formConfig.OutOfHome)}
                      error={errors.OutOfHome}
                      value={getFieldValue(data.OutOfHome)}
                      onChange={handleChange}
                      identical={compare("OutOfHome")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="Strengths"
                      label={getFieldLabel(formConfig.Strengths)}
                      error={errors.Strengths}
                      value={getFieldValue(data.Strengths)}
                      onChange={handleChange}
                      identical={compare("Strengths")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="Coordination"
                      label={getFieldLabel(formConfig.Coordination)}
                      error={errors.Coordination}
                      value={getFieldValue(data.Coordination)}
                      onChange={handleChange}
                      identical={compare("Coordination")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="Considerations"
                      label={getFieldLabel(formConfig.Considerations)}
                      error={errors.Considerations}
                      value={getFieldValue(data.Considerations)}
                      onChange={handleChange}
                      identical={compare("Considerations")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="Desire"
                      label={getFieldLabel(formConfig.Desire)}
                      error={errors.Desire}
                      value={getFieldValue(data.Desire)}
                      onChange={handleChange}
                      identical={compare("Desire")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div>
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.Disposition)}
                  </div>
                  <div>
                    <FormInput
                      name="Summary"
                      label={getFieldLabel(formConfig.Summary)}
                      error={errors.Summary}
                      value={getFieldValue(data.Summary)}
                      onChange={handleChange}
                      identical={compare("Summary")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="Interventions"
                      label={getFieldLabel(formConfig.Interventions)}
                      error={errors.Interventions}
                      value={getFieldValue(data.Interventions)}
                      onChange={handleChange}
                      identical={compare("Interventions")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="SafetyPlan"
                      label={getFieldLabel(formConfig.SafetyPlan)}
                      error={errors.SafetyPlan}
                      value={getFieldValue(data.SafetyPlan)}
                      onChange={handleChange}
                      identical={compare("SafetyPlan")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="ROI"
                      label={getFieldLabel(formConfig.ROI)}
                      error={errors.ROI}
                      value={getFieldValue(data.ROI)}
                      onChange={handleChange}
                      identical={compare("ROI")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="MeansRestriction"
                      label={getFieldLabel(formConfig.MeansRestriction)}
                      error={errors.MeansRestriction}
                      value={getFieldValue(data.MeansRestriction)}
                      onChange={handleChange}
                      identical={compare("MeansRestriction")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="M1Need"
                      label={getFieldLabel(formConfig.M1Need)}
                      error={errors.M1Need}
                      value={getFieldValue(data.M1Need)}
                      onChange={handleChange}
                      identical={compare("M1Need")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                  <div>
                    <FormInput
                      name="CareCoordination"
                      label={getFieldLabel(formConfig.CareCoordination)}
                      subLabel={formConfig.CareCoordination?.subLabel}
                      error={errors.CareCoordination}
                      value={getFieldValue(data.CareCoordination)}
                      onChange={handleChange}
                      identical={compare("CareCoordination")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                      subLabelPosition="label"
                    />
                    <div>
                      <div>
                        <FormRadioButton
                          name="BHAppointment"
                          label={getFieldLabel(formConfig.BHAppointment)}
                          error={errors.BHAppointment}
                          value={getFieldValue(data.BHAppointment)}
                          options={yesNoOptions}
                          onChange={handleChange}
                          identical={compare("BHAppointment")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <FormSelect
                      name="LevelOfCare"
                      label={getFieldLabel(formConfig.LevelOfCare)}
                      error={errors.LevelOfCare}
                      value={getFieldValue(data.LevelOfCare)}
                      options={getFieldOptions(formConfig.LevelOfCare)}
                      onChange={handleChange}
                      identical={compare("LevelOfCare")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                    {[
                      "WIC",
                      "CSU",
                      "ATU",
                      "ED",
                      "Inpatient treatment",
                    ].includes(getFieldValue(data.LevelOfCare)) ? (
                      <>
                        <FormSelect
                          name="Transportation"
                          label={getFieldLabel(formConfig.Transportation)}
                          error={errors.Transportation}
                          value={getFieldValue(data.Transportation)}
                          options={getFieldOptions(data.Transportation)}
                          onChange={handleChange}
                          identical={compare("Transportation")}
                          commentsHandler={handleComments}
                          disabled={formDisabled}
                        />
                        {getFieldValue(data.Transportation) ===
                        "Law Enforcement" ? (
                          <FormInput
                            name="CalledPolice"
                            label={getFieldLabel(formConfig.CalledPolice)}
                            error={errors.CalledPolice}
                            value={getFieldValue(data.CalledPolice)}
                            onChange={handleChange}
                            identical={compare("CalledPolice")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                        {getFieldValue(data.Transportation) === "Modality" ? (
                          <FormInput
                            name="ModalityPhone"
                            label={getFieldLabel(formConfig.ModalityPhone)}
                            error={errors.ModalityPhone}
                            value={getFieldValue(data.ModalityPhone)}
                            onChange={handleChange}
                            identical={compare("ModalityPhone")}
                            commentsHandler={handleComments}
                            disabled={formDisabled}
                          />
                        ) : null}
                      </>
                    ) : null}
                  </div>
                </div>
                <div>
                  <div className="fw-bold">
                    {getFieldLabel(formConfig.FollowUp)}
                  </div>
                  <div>{getFieldLabel(formConfig.PeerSupport)}</div>
                  <div>
                    <FormInput
                      name="CalledFollowUp"
                      label={getFieldLabel(formConfig.CalledFollowUp)}
                      error={errors.CalledFollowUp}
                      value={getFieldValue(data.CalledFollowUp)}
                      onChange={handleChange}
                      identical={compare("CalledFollowUp")}
                      commentsHandler={handleComments}
                      disabled={formDisabled}
                    />
                  </div>
                </div>
                <div>
                  <FormInput
                    name="AdditionalNotes"
                    label={getFieldLabel(formConfig.AdditionalNotes)}
                    error={errors.AdditionalNotes}
                    value={getFieldValue(data.AdditionalNotes)}
                    onChange={handleChange}
                    identical={compare("AdditionalNotes")}
                    commentsHandler={handleComments}
                    labelClassName="fw-bold"
                    disabled={formDisabled}
                  />
                </div>
              </div>
            </>
          ) : null}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={helperConfiguration.finalSignType}
              initialSignatureType={helperConfiguration.signatureType}
            />
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            />
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              />
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default COMC18Assessment;
